import {
  BILLING_MODULE_KEYS,
  type BillingModuleKey,
} from '@bill-alps/backend/features/companies/modules';
import {
  FIDUCIAIRE_MODULE_TIERS,
  FIDUCIAIRE_OVERAGE,
  FIDUCIAIRE_USAGE_FEATURES,
  fiduciaireModuleRate,
} from '@bill-alps/backend/features/fiduciaire/plans';
import { Badge, cn, TruncatedTableContainer } from '@bill-alps/ui';
import { useTranslation } from '../i18n/useTranslation';
import { formatCHF } from '@/lib/format/currency';
import type { ModuleQuantities } from '@/lib/billing/fiduciaire-billing';

interface FiduciaireTierTableProps {
  /**
   * Billed mandates per module. Each module sits in its own tier, so the
   * table marks the row of every module with at least one billed mandate.
   */
  moduleQuantities?: ModuleQuantities;
  /** Hide the per-month allowances footer (the dialogs are tight on space). */
  showOverage?: boolean;
  className?: string;
}

/**
 * The fiduciaire grid: CHF per mandate per MODULE, degressive on the number
 * of mandates billed for that module. Volume tiers — the rate of the tier
 * applies to every mandate of the module, not just the ones above the bound.
 */
export function FiduciaireTierTable({
  moduleQuantities,
  showOverage = true,
  className,
}: FiduciaireTierTableProps) {
  const { t } = useTranslation('fiduciaire');
  const billedModules = BILLING_MODULE_KEYS.filter(
    (module) => (moduleQuantities?.[module] ?? 0) > 0,
  );
  const modulesAtRate = (rate: number): BillingModuleKey[] =>
    billedModules.filter(
      (module) =>
        fiduciaireModuleRate(moduleQuantities?.[module] ?? 0) === rate,
    );

  return (
    <TruncatedTableContainer
      className={cn('overflow-hidden rounded-md border', className)}
    >
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/50 text-left text-xs text-muted-foreground">
            <th className="px-3 py-2 font-medium">
              {t('tiers.mandatesHeader')}
            </th>
            <th className="px-3 py-2 text-right font-medium">
              {t('tiers.rateHeader')}
            </th>
            {billedModules.length > 0 && (
              <th className="px-3 py-2 text-right font-medium">
                {t('tiers.yourModulesHeader')}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {FIDUCIAIRE_MODULE_TIERS.map((tier, index) => {
            const from =
              index === 0
                ? 1
                : (FIDUCIAIRE_MODULE_TIERS[index - 1].upTo ?? 0) + 1;
            const range =
              tier.upTo === null
                ? t('tiers.rangeOpen', { from })
                : t('tiers.range', { from, to: tier.upTo });
            const here = modulesAtRate(tier.ratePerMandate);
            return (
              <tr
                key={`${tier.upTo ?? 'inf'}`}
                data-testid="fiduciaire-tier-row"
                className={cn(
                  'border-b last:border-b-0',
                  here.length > 0 && 'bg-primary/5 font-medium',
                )}
              >
                <td className="px-3 py-2">{range}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {t('tiers.ratePerMandate', {
                    rate: formatCHF(tier.ratePerMandate),
                  })}
                </td>
                {billedModules.length > 0 && (
                  <td className="px-3 py-2 text-right">
                    <span className="inline-flex flex-wrap justify-end gap-1">
                      {here.map((module) => (
                        <Badge key={module} variant="outline" size="sm">
                          {t(`modules.${module}`)} ·{' '}
                          {moduleQuantities?.[module]}
                        </Badge>
                      ))}
                    </span>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="space-y-1 border-t bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
        <p>{t('tiers.perModuleNote')}</p>
        {showOverage && (
          <>
            <p className="pt-1 font-medium text-foreground/80">
              {t('tiers.overageTitle')}
            </p>
            <ul className="list-disc space-y-0.5 pl-4">
              {FIDUCIAIRE_USAGE_FEATURES.map((feature) => (
                <li key={feature}>
                  {t(`tiers.overage.${feature}`, {
                    included: FIDUCIAIRE_OVERAGE[feature].includedPerMonth,
                    price: formatCHF(FIDUCIAIRE_OVERAGE[feature].chfPerExtra),
                  })}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </TruncatedTableContainer>
  );
}
